import React from 'react';
import {
  BoltIcon,
  EnvelopeIcon,
  ChartLineIcon,
  CodeBranchIcon,
  GearIcon
} from '@revfluence/fresh-icons/solid/esm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTags, faBagShopping, faUserPen, faShuffle, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';

// FontAwesome wrapper component for faTags
const FontAwesomeTagsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <FontAwesomeIcon icon={faTags} className={className} />
);

// FontAwesome wrapper component for faBagShopping
const FontAwesomeBagShoppingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <FontAwesomeIcon icon={faBagShopping} className={className} />
);

// FontAwesome wrapper component for faUserPen
const FontAwesomeUserPenIcon: React.FC<{ className?: string }> = ({ className }) => (
  <FontAwesomeIcon icon={faUserPen} className={className} />
);

// FontAwesome wrapper component for faShuffle
const FontAwesomeShuffleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <FontAwesomeIcon icon={faShuffle} className={className} />
);

// FontAwesome wrapper component for faClockRotateLeft
const FontAwesomeClockRotateLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <FontAwesomeIcon icon={faClockRotateLeft} className={className} />
);

import { AutomationActionNodeSubType, AutomationNodeType } from '@frontend/app/types/globalTypes';
import { AutomationNodeV2 } from '../types';
import { UPDATE_MEMBER_DATA_TYPES } from '../PropertyPanel/ActionTypes/UpdateMemberDataActionForm';

export interface NodeIconConfig {
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  backgroundColor: string;
}

// Centralized icon mapping for all automation nodes
export const NODE_ICON_MAPPING: Record<string, NodeIconConfig> = {
  // Node types
  DELAY: {
    icon: FontAwesomeClockRotateLeftIcon,
    color: 'text-white',
    backgroundColor: 'bg-yellow-5',
  },
  CONDITION: {
    icon: FontAwesomeShuffleIcon,
    color: 'text-white',
    backgroundColor: 'bg-pink-5',
  },
  TRIGGER: {
    icon: BoltIcon,
    color: 'text-white',
    backgroundColor: 'bg-lime-5',
  },
  BRANCH: {
    icon: CodeBranchIcon,
    color: 'text-white',
    backgroundColor: 'bg-purple-500',
  },

  // Action subtypes
  [AutomationActionNodeSubType.SEND_EMAIL]: {
    icon: EnvelopeIcon,
    color: 'text-white',
    backgroundColor: 'bg-purple-5',
  },
  [AutomationActionNodeSubType.UPDATE_MEMBER_DATA]: {
    icon: FontAwesomeUserPenIcon,
    color: 'text-white',
    backgroundColor: 'bg-teal-5',
  },
  [AutomationActionNodeSubType.ADD_TO_OFFER]: {
    icon: FontAwesomeTagsIcon,
    color: 'text-white',
    backgroundColor: 'bg-magenta-5',
  },
  [AutomationActionNodeSubType.SEND_PRODUCT_CATALOG]: {
    icon: FontAwesomeBagShoppingIcon,
    color: 'text-white',
    backgroundColor: 'bg-orange-5',
  },
  [AutomationActionNodeSubType.HANDLE_PFA_BOOST]: {
    icon: ChartLineIcon,
    color: 'text-white',
    backgroundColor: 'bg-green-600',
  },

  // Default fallbacks
  ACTION_DEFAULT: {
    icon: GearIcon,
    color: 'text-white',
    backgroundColor: 'bg-gray-500',
  },
  DEFAULT: {
    icon: GearIcon,
    color: 'text-white',
    backgroundColor: 'bg-gray-500',
  },
};

/**
 * Get icon configuration for a specific automation node or node type string
 */
export const getNodeIconConfig = (node: AutomationNodeV2 | { type: string; subType?: string }): NodeIconConfig => {
  // Handle string-based type for backwards compatibility
  if ('type' in node && typeof node.type === 'string') {
    // For ACTION nodes, use subType for specific mapping
    if (node.type === 'ACTION' && node.subType) {
      return NODE_ICON_MAPPING[node.subType] || NODE_ICON_MAPPING.ACTION_DEFAULT;
    }

    // For other node types, use type directly
    return NODE_ICON_MAPPING[node.type] || NODE_ICON_MAPPING.DEFAULT;
  }

  // Fallback
  return NODE_ICON_MAPPING.DEFAULT;
};

/**
 * Render a node icon with consistent styling
 */
export const NodeIcon: React.FC<{
  node: AutomationNodeV2 | { type: string; subType?: string };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ node, size = 'md', className = '' }) => {
  const config = getNodeIconConfig(node);
  const IconComponent = config.icon;

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  const iconSizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-5 h-5',
  };

  return (
    <div className={`${config.backgroundColor} flex ${sizeClasses[size]} items-center justify-center relative rounded-lg ${className}`}>
      <IconComponent className={`${iconSizeClasses[size]} ${config.color}`} />
    </div>
  );
};

/**
 * Get just the icon component for use in other contexts
 */
export const getNodeIcon = (node: AutomationNodeV2 | { type: string; subType?: string }) => {
  const config = getNodeIconConfig(node);
  return config.icon;
};

/**
 * Get the background color class for a node
 */
export const getNodeBackgroundColor = (node: AutomationNodeV2 | { type: string; subType?: string }): string => {
  const config = getNodeIconConfig(node);
  return config.backgroundColor;
};

/**
 * Get the text color class for a node
 */
export const getNodeTextColor = (node: AutomationNodeV2 | { type: string; subType?: string }): string => {
  const config = getNodeIconConfig(node);
  return config.color;
};

/**
 * Get display label for automation nodes based on type and metadata
 * This is a shared utility used by both ActionNodeV2 and PropertyPanel
 */
export const getNodeDisplayLabel = (
  nodeType: string,
  subType?: AutomationActionNodeSubType,
  metadata?: any
): string => {
  switch (nodeType) {
    case AutomationNodeType.DELAY:
      return 'Time Delay';
    case AutomationNodeType.CONDITION:
      return 'Add Condition';
    case AutomationNodeType.ACTION:
      switch (subType) {
        case AutomationActionNodeSubType.SEND_EMAIL:
          if (metadata.sendPFACatalog) {
            return 'Select Product Catalog & Send Email';
          }
          return 'Send Email';
        case AutomationActionNodeSubType.HANDLE_PFA_BOOST:
          return 'Apply Boost';
        case AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST:
          return 'Cashback Boost';
        case AutomationActionNodeSubType.UPDATE_MEMBER_DATA:
          // Return specific label based on update type
          if (metadata?.type) {
            switch (metadata.type) {
              case UPDATE_MEMBER_DATA_TYPES.ADD_TAG:
                return 'Tag Member';
              case UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP:
                return 'Add to Group';
              case UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION:
                return 'Update Custom Field';
              // Handle legacy format
              case 'TAG':
                return 'Tag Member';
              case 'GROUP':
                return 'Add to Group';
              default:
                return 'Update Member Data';
            }
          }
          return 'Update Member Data';
        case AutomationActionNodeSubType.ADD_TO_OFFER:
          return 'Add to Offer';
        case AutomationActionNodeSubType.SEND_PRODUCT_CATALOG:
          return 'Send Product Catalog';
        default:
          return 'Action';
      }
    default:
      return 'Properties';
  }
};

/**
 * Get the failed node type from error metadata
 */
export const getFailedNodeType = (errorMetadata: string | undefined): string | null => {
  if (!errorMetadata) return null;

  try {
    const metadata = JSON.parse(errorMetadata);
    if (metadata.actionType) {
      // Convert action type to user-friendly name
      switch (metadata.actionType) {
        case 'DEBUG':
          return 'Debug';
        case 'SEND_EMAIL':
          return 'Send Email';
        case 'HANDLE_AUTOMATED_PAYOUT':
          return 'Handle Automated Payout';
        case 'HANDLE_DISCOUNT_CODE_BOOST':
          return 'Handle Discount Code Boost';
        case 'HANDLE_PFA_BOOST':
          return 'Handle PFA Boost';
        case 'SEND_STA_EMAILS':
          return 'Send STA Emails';
        case 'HANDLE_CASHBACK_BOOST':
          return 'Handle Cashback Boost';
        case 'UPDATE_MEMBER_DATA':
          return 'Update Member Data';
        case 'ADD_TO_OFFER':
          return 'Add to Offer';
        case 'SEND_OFFER_DETAILS':
          return 'Send Offer Details';
        case 'SEND_PRODUCT_CATALOG':
          return 'Send Product Catalog';
        case 'CONDITION':
          return 'Condition Check';
        case 'DELAY':
          return 'Delay';
        default:
          return metadata.actionType.replace(/_/g, ' ').toLowerCase()
            .replace(/\b\w/g, (l: string) => l.toUpperCase());
      }
    }
  } catch (e) {
    // If parsing fails, return null
    return null;
  }

  return null;
};
